import { Fragment } from 'react';
import { cn } from '@/lib/utils';
import { rotuloSafra, type BaixaDoMes } from '@/hooks/useFaturasMes';

/**
 * A BAIXA DAS CONTAS DO MÊS (Douglas 24/09/2026). Uma faixa de UMA linha, no
 * molde da barra de etapas da Visão Prime — não uma tabela esticada. Mostra o
 * mês escolhido; em "Todas", a soma dos meses. A triagem e a Visão Prime leem
 * a mesma baixaPorSafra, então os números batem.
 */
export function BaixaContasTabela({ linhas, safra, className, filtro, onFiltrar }: {
  linhas: BaixaDoMes[]; safra: string; className?: string;
  /** Só a triagem passa: Faltam baixar e Bee não consegue filtram a lista. A Visão Prime não muda. */
  filtro?: '' | 'faltam' | 'bee'; onFiltrar?: (f: 'faltam' | 'bee') => void;
}) {
  const sel = safra === 'todas' ? linhas : linhas.filter(b => b.safra === safra);
  const soma = (k: 'contas' | 'baixadas' | 'existem' | 'faltam' | 'naoExistem' | 'beeNaoConsegue') => sel.reduce((s, b) => s + b[k], 0);
  const contas = soma('contas');
  const baixadas = soma('baixadas');
  const existem = soma('existem');
  const faltam = soma('faltam');
  const naoExistem = soma('naoExistem');
  const beeNaoConsegue = soma('beeNaoConsegue');
  const pctFalta = existem > 0 ? faltam / existem : null;
  const n = (v: number) => v.toLocaleString('pt-BR');
  const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
  const celulas: { rotulo: string; valor: string; cor?: string; titulo?: string; filtra?: 'faltam' | 'bee' }[] = [
    { rotulo: 'Contas do mês', valor: n(contas) },
    { rotulo: 'Já baixadas', valor: n(baixadas), cor: 'text-success' },
    { rotulo: 'Ainda não existem', valor: n(naoExistem), cor: 'text-muted-label', titulo: 'A Claro ainda não fechou a conta (~23 dias depois da ativação). Não entra no %.' },
    { rotulo: 'Faltam baixar', valor: n(faltam), cor: faltam > 0 ? 'text-danger' : undefined, filtra: 'faltam' },
    { rotulo: 'Bee não consegue', valor: n(beeNaoConsegue), cor: beeNaoConsegue > 0 ? 'text-danger' : undefined,
      titulo: 'O Bee recusou todas as linhas destas contas. Baixe no SOLAR e anexe, ou informe outra linha.', filtra: 'bee' },
    { rotulo: '% que falta', valor: pct(pctFalta), cor: pctFalta ? 'text-danger' : undefined, titulo: 'Faltam baixar ÷ contas que já existem' },
  ];
  if (!contas) return null;
  return (
    <div className={cn('flex h-[52px] flex-shrink-0 items-center rounded-[11px] border border-border-warm bg-card px-[5px]', className)}>
      <div className="w-[150px] flex-shrink-0 px-2">
        <div className="truncate text-[9.5px] font-bold uppercase tracking-[1.1px] text-muted-label">Baixa das contas</div>
        <div className="truncate text-[12px] font-bold text-card-foreground">{safra === 'todas' ? 'todos os meses' : rotuloSafra(safra)}</div>
        <div className="mt-1 flex h-[4px] overflow-hidden rounded-full bg-border" aria-hidden="true">
          <i className="block bg-[#075E54] dark:bg-[#25D366]" style={{ width: `${contas ? (baixadas / contas) * 100 : 0}%` }} />
          <i className="block bg-primary" style={{ width: `${contas ? (faltam / contas) * 100 : 0}%` }} />
        </div>
      </div>
      {celulas.map(c => {
        const conteudo = <>
          <div className={cn('truncate font-display text-[16px] font-bold leading-tight tracking-[-0.4px] tabular-nums', c.cor ?? 'text-card-foreground')}>{c.valor}</div>
          <div className="truncate text-[9.5px] font-bold uppercase tracking-[1.1px] text-muted-label">{c.rotulo}</div>
        </>;
        const filtra = c.filtra;
        return <Fragment key={c.rotulo}>
          <span className="h-6 w-px flex-shrink-0 bg-border" />
          {onFiltrar && filtra
            ? <button type="button" aria-pressed={filtro === filtra} onClick={() => onFiltrar(filtra)} title={`${c.titulo ? `${c.titulo} · ` : ''}Clique para ver só estas contas`}
                className={cn('min-w-0 flex-1 rounded-md px-2 py-0.5 text-center hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-ring',
                  filtro === filtra && 'bg-primary/5')}>{conteudo}</button>
            : <div className="min-w-0 flex-1 px-2 text-center" title={c.titulo}>{conteudo}</div>}
        </Fragment>;
      })}
    </div>
  );
}
